import type { U2000TableColumn } from '../u2000Core/components/tables/basic/U2000BasicTableList';
import type { U3146PreviewGridRow } from './components/wizard/u3146InitCloseInactivePreviewGrid';

export type U3146PreviewGridSortField = {
  name: string;
  sortOrder?: string;
};

/** Montant utilisé pour le tri TEC ; null si la ligne n'a pas de colonnes TEC ou pas de montant. */
export function u3146PreviewGridRowSortableTecAmount(row: U3146PreviewGridRow): number | null {
  if (!row.showsTecColumns) {
    return null;
  }
  if (row.uninvoicedTecAmountTec == null || Number.isNaN(row.uninvoicedTecAmountTec)) {
    return null;
  }
  return row.uninvoicedTecAmountTec;
}

function compareNullableNumber(a: number | null, b: number | null, asc: boolean): number {
  const aMissing = a == null;
  const bMissing = b == null;
  if (aMissing && bMissing) {
    return 0;
  }
  if (aMissing) {
    return asc ? 1 : -1;
  }
  if (bMissing) {
    return asc ? -1 : 1;
  }
  if (a === b) {
    return 0;
  }
  return asc ? a - b : b - a;
}

function compareRowValues(
  rowA: U3146PreviewGridRow,
  rowB: U3146PreviewGridRow,
  fieldName: string,
  column: U2000TableColumn | undefined,
  asc: boolean,
): number {
  if (fieldName === 'uninvoicedTecAmountTec') {
    return compareNullableNumber(
      u3146PreviewGridRowSortableTecAmount(rowA),
      u3146PreviewGridRowSortableTecAmount(rowB),
      asc,
    );
  }

  const aVal = rowA[fieldName as keyof U3146PreviewGridRow];
  const bVal = rowB[fieldName as keyof U3146PreviewGridRow];
  const type = column?.type ?? 'string';

  if (type === 'number') {
    const aNum = typeof aVal === 'number' ? aVal : aVal == null ? null : Number(aVal);
    const bNum = typeof bVal === 'number' ? bVal : bVal == null ? null : Number(bVal);
    const aNaN = aNum == null || Number.isNaN(aNum);
    const bNaN = bNum == null || Number.isNaN(bNum);
    return compareNullableNumber(aNaN ? null : aNum, bNaN ? null : bNum, asc);
  }

  const aStr = aVal == null ? '' : String(aVal);
  const bStr = bVal == null ? '' : String(bVal);
  const cmp = aStr.localeCompare(bStr, undefined, { sensitivity: 'base' });
  return asc ? cmp : -cmp;
}

/**
 * Tri client des lignes preview (mono-colonne, ordre U2000 ASC/DESC).
 */
export function u3146SortPreviewGridRows(
  rows: U3146PreviewGridRow[],
  sortedFields: U3146PreviewGridSortField[],
  columns: U2000TableColumn[],
): U3146PreviewGridRow[] {
  const sortSpec = sortedFields?.[0];
  if (!sortSpec?.name) {
    return rows;
  }
  const asc = sortSpec.sortOrder !== 'DESC';
  const columnMap = new Map(columns.map((col) => [col.name, col]));
  const column = columnMap.get(sortSpec.name);
  const fieldName = sortSpec.name;

  return [...rows].sort((rowA, rowB) => {
    const cmp = compareRowValues(rowA, rowB, fieldName, column, asc);
    if (cmp !== 0) {
      return cmp;
    }
    return rowA.projectId.localeCompare(rowB.projectId);
  });
}
